import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AppBlockingIcon from "@mui/icons-material/AppBlocking";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";

const AppBlockingDialog = ({ open, onClose, row, onSuccess, defaultSelected = [] }) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedNames(defaultSelected);
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const res = await axiosConfig({ method: "get", url: "/payment/plans" });
        const allPlans = res?.data?.response?.data || res?.data?.data || [];
        setPlans(allPlans);
      } catch {
        toast.error("Failed to load plans");
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, [open]);

  const allSelected = plans.length > 0 && selectedNames.length === plans.length;
  const someSelected = selectedNames.length > 0 && !allSelected;

  const handleSelectAll = (e) => {
    setSelectedNames(e.target.checked ? plans.map((p) => p.plan_code) : []);
  };

  const handleToggle = (plan_code) => {
    setSelectedNames((prev) =>
      prev.includes(plan_code) ? prev.filter((x) => x !== plan_code) : [...prev, plan_code]
    );
  };

  const handleBlock = async () => {
    setBlockLoading(true);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "/home/features-tag-with-plan/",
        data: {
          screen_id: row.id,
          plan_list: selectedNames,
        },
      });
      const msg = res?.data?.message || res?.data?.response || "Item blocked successfully!";
      toast.success(msg);
      onClose();
      onSuccess?.(selectedNames);
    } catch (e) {
      const errMsg =
        e.response?.data?.message ||
        e.response?.data?.response ||
        e.response?.data?.detail ||
        e.message ||
        "Failed to block item";
      toast.error(errMsg);
    } finally {
      setBlockLoading(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, background: "#04343a", display: "flex", alignItems: "center", gap: 1.5 }}>
        <AppBlockingIcon sx={{ color: "#f87171", fontSize: 22 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
            Block App Item
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
            {row.name} &mdash; {row.key}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
        <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5 }}>
          Select plans to apply blocking for this item:
        </Typography>

        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
          {/* Select All */}
          <Box
            sx={{
              px: 2, py: 1,
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  disabled={plansLoading || plans.length === 0}
                  sx={{
                    color: "#016551",
                    "&.Mui-checked": { color: "#016551" },
                    "&.MuiCheckbox-indeterminate": { color: "#016551" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                  Select All {plans.length > 0 && `(${plans.length})`}
                </Typography>
              }
              sx={{ m: 0 }}
            />
            {selectedNames.length > 0 && (
              <Typography sx={{ ml: "auto", fontSize: 11, color: "#016551", fontWeight: 600 }}>
                {selectedNames.length} selected
              </Typography>
            )}
          </Box>

          {/* Plan Items */}
          <Box sx={{ maxHeight: 360, overflowY: "auto", p: 1.5 }}>
            {plansLoading ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} sx={{ color: "#04343a" }} />
              </Box>
            ) : plans.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
                No plans found
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {plans.map((plan) => {
                  const selected = selectedNames.includes(plan.plan_code);
                  return (
                    <Box
                      key={plan.id}
                      onClick={() => handleToggle(plan.plan_code)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.8,
                        border: selected ? "1.5px solid #016551" : "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        background: selected ? "#f0fdf4" : "#fff",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#016551", background: "#f0fdf4" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selected}
                        onChange={() => handleToggle(plan.plan_code)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          p: 0,
                          color: "#016551",
                          "&.Mui-checked": { color: "#016551" },
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.3 }}>
                          {plan.name}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4, flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: selected ? "#016551" : "#64748b",
                              background: selected ? "#dcfce7" : "#f1f5f9",
                              borderRadius: "4px",
                              px: 0.7,
                              py: 0.15,
                              letterSpacing: "0.03em",
                            }}
                          >
                            {plan.plan_code}
                          </Typography>
                          {plan.price && (
                            <Typography sx={{ fontSize: 10, color: "#64748b" }}>· ₹{plan.price}</Typography>
                          )}
                          {plan.interval && plan.interval_unit && (
                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                              · {plan.interval} {plan.interval_unit}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          size="small"
          disabled={blockLoading}
          sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleBlock}
          disabled={blockLoading || selectedNames.length === 0}
          startIcon={blockLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
          sx={{
            borderRadius: "8px",
            background: "#dc2626",
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            "&:hover": { background: "#b91c1c" },
            "&:disabled": { background: "#94a3b8", boxShadow: "none" },
          }}
        >
          {blockLoading ? "Blocking…" : `Block${selectedNames.length > 0 ? ` (${selectedNames.length})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppBlockingDialog;
